/**
 * src/core/events/EventBus.ts
 *
 * Strongly-typed Central Event Bus implementing Observer/Pub-Sub.
 * Single source of truth for all cross-cutting domain events.
 */

import { CoreEventMap } from './EventTypes';

type EventCallback<T> = (payload: T) => void | Promise<void>;

export class EventBus {
  private static instance: EventBus;
  private listeners: Map<string, Set<EventCallback<any>>> = new Map();

  private constructor() {}

  public static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  public on<K extends keyof CoreEventMap>(
    event: K,
    handler: EventCallback<CoreEventMap[K]>
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);

    return () => {
      this.listeners.get(event)?.delete(handler);
      if (this.listeners.get(event)?.size === 0) {
        this.listeners.delete(event);
      }
    };
  }

  public emit<K extends keyof CoreEventMap>(event: K, payload: CoreEventMap[K]): void {
    const handlers = this.listeners.get(event);
    if (!handlers || handlers.size === 0) return;

    handlers.forEach((h) => {
      try {
        h(payload);
      } catch (err) {
        console.error(`[EventBus] Unhandled error in listener for ${event}:`, err);
      }
    });
  }

  public clear(): void {
    this.listeners.clear();
  }

  public listenerCount<K extends keyof CoreEventMap>(event: K): number {
    return this.listeners.get(event)?.size || 0;
  }
}

export const eventBus = EventBus.getInstance();
